'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { gql, useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';
import { Stack, Container } from '@mantine/core';

import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import ResourceNameChange, {
  type ResourceNameChangeRef,
} from '@features/Resource/ResourceNameChange';

import dialogStoreVar from '@features/Dialog/store/dialogStore';

import { useApplyRenameSuggestionsMutation } from '@app-types/blackroom-api';
import { DialogResourceEditProps } from './types';
import type { ResourceNameChangeItem } from '@features/Resource/ResourceNameChange/types';

import * as classes from './styles/index.css';
import useWorkspace from '@app/features/Workspace/store/hooks/useWorkspace';

function DialogResourceEdit({
  dialogResources,
  ...props
}: DialogResourceEditProps) {
  const workspace = useWorkspace();
  const resources = dialogResources || [];

  const resourceNameChangeRef = useRef<ResourceNameChangeRef>(null);

  const [resourceChanges, setResourceChanges] = useState<
    Map<string, ResourceNameChangeItem>
  >(new Map());
  const [isShowingResults, setIsShowingResults] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [activeTab, setActiveTab] = useState('automatic');

  const dialogStore = useReactiveVar(dialogStoreVar);
  const { closeDialog } = dialogStore ?? {};

  const [applyRenameSuggestions] = useApplyRenameSuggestionsMutation();

  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      resourceNameChangeRef.current?.cancelAllRequests();
    };
  }, []);

  const handleChangesUpdate = useCallback(
    (changes: Map<string, ResourceNameChangeItem>) => {
      setResourceChanges(changes);
    },
    [],
  );

  const handleResultsStepChange = useCallback((showing: boolean) => {
    setIsShowingResults(showing);
  }, []);

  const handleSelectedPromptChange = useCallback((prompt: string) => {
    setSelectedPrompt(prompt);
  }, []);

  const handleActiveTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
  }, []);

  const handleBack = useCallback(() => {
    resourceNameChangeRef.current?.goBackToPreviousStep();
  }, []);

  async function onSubmit() {
    closeDialog?.('dialog-resource-edit');

    try {
      const changeEntries = Array.from(resourceChanges.entries());

      await applyRenameSuggestions({
        variables: {
          applyRenameSuggestionsInput: {
            workspaceId: workspace.id,
            suggestions: changeEntries.map(([resourceId, change]) => ({
              resourceId: resourceId,
              newName: change.newName,
            })),
          },
        },
        update: (cache, { data }) => {
          const renamedResources = data?.applyRenameSuggestions ?? [];

          renamedResources.forEach((renamedResource) => {
            cache.writeFragment({
              id: cache.identify({
                __typename: 'ResourceIndexView',
                id: renamedResource.id,
              }),
              fragment: gql`
                fragment UpdatedResourceName on ResourceIndexView {
                  name
                }
              `,
              data: {
                name: renamedResource.name,
              },
            });
          });
        },
      });
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
      console.error(error);
    }
  }

  function handleApply() {
    if (selectedPrompt) {
      resourceNameChangeRef.current?.generateAll(selectedPrompt);
    }
  }

  const isFormValid = () => {
    for (const resource of resources) {
      const change = resourceChanges.get(resource?.id || '');
      if (!change) return false;

      const finalName = change.newName;
      if (finalName.trim() === '') {
        return false;
      }
    }

    return true;
  };

  const isAutomatic = activeTab === 'automatic';
  const hasSelectedPrompt = !!selectedPrompt;

  const resourceInputs = resources.map((resource) => ({
    id: resource?.id || '',
    name: resource?.name || '',
    type: resource?.type || '',
    workspaceId: resource?.workspaceId || '',
    mimeType: resource?.mimeType,
  }));

  return (
    <Container className={classes.dialogResourceEdit} fluid>
      <Stack align="stretch" justify="space-between" gap="lg" h="100%">
        <ResourceNameChange
          ref={resourceNameChangeRef}
          resources={resourceInputs}
          onChangesUpdate={handleChangesUpdate}
          onResultsStepChange={handleResultsStepChange}
          onSelectedPromptChange={handleSelectedPromptChange}
          onActiveTabChange={handleActiveTabChange}
          maxConcurrent={5}
        />

        {isShowingResults ? (
          <ConfirmBar
            labelProps={{
              confirm: t('Common:cta.saveChanges'),
              cancel: t('Common:cta.back'),
            }}
            onConfirm={onSubmit}
            onCancel={handleBack}
            disabled={!isFormValid()}
          />
        ) : (
          <ConfirmBar
            labelProps={{
              confirm: isAutomatic
                ? t('Resource:DialogResourceEdit.generateSuggestions')
                : t('Common:cta.saveChanges'),
              cancel: t('Common:cta.cancel'),
            }}
            onConfirm={isAutomatic ? handleApply : onSubmit}
            onCancel={() => closeDialog?.('dialog-resource-edit')}
            disabled={isAutomatic ? !hasSelectedPrompt : !isFormValid()}
          />
        )}
      </Stack>
    </Container>
  );
}

export default DialogResourceEdit;
